import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

type Point = { x: number; y: number };

type Options = {
  /** Initial top/left in CSS pixels */
  initial?: Point;
  /** Keep a few pixels visible inside the viewport */
  margin?: number;
  /** Persist position across reloads */
  storageKey?: string;
};

type HeaderBind = {
  onPointerDown: (e: React.PointerEvent) => void;
  role: string;
  "aria-grabbed": boolean;
  style?: React.CSSProperties;
};

export function useDraggableFixed(options: Options = {}) {
  const { initial = { x: 24, y: 24 }, margin = 8, storageKey } = options;

  const [pos, setPos] = useState<Point>(() => {
    if (storageKey) {
      const saved = JSON.parse(localStorage.getItem(storageKey) || "null") as Point | null;
      if (saved && typeof saved.x === "number" && typeof saved.y === "number") {
        return saved;
      }
    }
    return initial;
  });

  const panelRef = useRef<HTMLDivElement | null>(null);
  const dragging = useRef(false);
  const dragStart = useRef<{ pointerId: number; start: Point; origin: Point } | null>(null);
  const raf = useRef<number | null>(null);

  // Clamp a point so the panel stays inside the viewport with a margin.
  const clampToViewport = useCallback(
    (p: Point): Point => {
      const w = window.innerWidth;
      const h = window.innerHeight;

      // Try to use the panel dimensions if mounted; fall back to a reasonable size.
      const rect = panelRef.current?.getBoundingClientRect();
      const panelW = rect?.width ?? 320;
      const panelH = rect?.height ?? 200;

      const minX = margin;
      const minY = margin;
      const maxX = Math.max(margin, w - panelW - margin);
      const maxY = Math.max(margin, h - panelH - margin);

      return { x: Math.min(Math.max(p.x, minX), maxX), y: Math.min(Math.max(p.y, minY), maxY) };
    },
    [margin],
  );

  // Persist if requested
  useEffect(() => {
    if (!storageKey) return;
    localStorage.setItem(storageKey, JSON.stringify(pos));
  }, [pos, storageKey]);

  // Keep visible on resize/zoom changes
  useEffect(() => {
    const onResize = () => setPos((prev) => clampToViewport(prev));
    window.addEventListener("resize", onResize);
    // Some browsers fire "orientationchange" without resize quickly:
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [clampToViewport]);

  // Ensure initial clamp after mount (when we know actual size)
  useLayoutEffect(() => {
    setPos((prev) => clampToViewport(prev));
  }, [clampToViewport]);

  const endDrag = useCallback((e?: PointerEvent) => {
    if (!dragging.current) return;
    dragging.current = false;

    // Release capture if we started with a header
    if (dragStart.current?.pointerId != null && (e?.target as Element | undefined)?.releasePointerCapture) {
      (e!.target as Element).releasePointerCapture(dragStart.current.pointerId);
    }

    dragStart.current = null;
    if (raf.current != null) {
      cancelAnimationFrame(raf.current);
      raf.current = null;
    }
  }, []);

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      if (!dragging.current || !dragStart.current) return;

      // Avoid text selection/scroll while dragging
      e.preventDefault();

      const dx = e.clientX - dragStart.current.start.x;
      const dy = e.clientY - dragStart.current.start.y;
      const next = clampToViewport({ x: dragStart.current.origin.x + dx, y: dragStart.current.origin.y + dy });

      if (raf.current == null) {
        raf.current = requestAnimationFrame(() => {
          setPos(next);
          raf.current = null;
        });
      }
    },
    [clampToViewport],
  );

  useEffect(() => {
    const up = (e: PointerEvent) => endDrag(e);
    const move = (e: PointerEvent) => onPointerMove(e);

    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);

    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  }, [onPointerMove, endDrag]);

  // Bind this to any header element that should initiate dragging.
  const getHeaderBind = useCallback(
    (): HeaderBind => ({
      onPointerDown: (e) => {
        // Only left button or touch/pen
        if (e.button !== 0 && e.pointerType === "mouse") return;

        dragging.current = true;
        dragStart.current = {
          pointerId: e.pointerId,
          start: { x: e.clientX, y: e.clientY },
          origin: { ...pos },
        };

        // Improve capture so we still receive move/up if pointer leaves the header
        (e.target as Element).setPointerCapture(e.pointerId);

        // Prevent selecting text while dragging starts
        e.preventDefault();
      },
      role: "button",
      "aria-grabbed": dragging.current,
      style: { cursor: "grab", userSelect: "none", touchAction: "none" },
    }),
    [pos],
  );

  // Convenience styles/props for the panel itself
  const panelStyle: React.CSSProperties = {
    position: "fixed",
    left: `${pos.x}px`,
    top: `${pos.y}px`,
    // Optional: improve dragging feel on mobile
    touchAction: "none",
  };

  return {
    /** Attach to the floating panel (div) */
    panelRef,
    /** Inline style for the panel (position: fixed; left/top) */
    panelStyle,
    /** Current position */
    position: pos,
    /** Programmatically move the panel (will clamp into view) */
    setPosition: (p: Point) => setPos(clampToViewport(p)),
    /** Bind to one or more header elements to make them draggable handles */
    getHeaderBind,
  };
}
